import { type Order, OrderStatus } from 'api/types/order.types';
import type { TFunction } from 'i18next';
import { usePastActiveOrders } from 'pages/queries/order.queries';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const FLOATER_ORDER_STATE = {
  NORMAL: 0,
  WARNING: 1,
  ERROR: 2,
};
const statusesToFilter = [
  OrderStatus.ITEMS_PENDING_APPROVAL,
  OrderStatus.ORDER_PARTIALLY_DELIVERED,
  OrderStatus.PENDING_ITEMS_DELIVERY,
  OrderStatus.EXTRA_ITEM_COLLECTION,
  OrderStatus.PAYMENT_FAILED,
  OrderStatus.DROPOFF_FAILED,
  OrderStatus.EXTRA_ITEM_COLLECTION_FAILED,
  OrderStatus.PENDING_ITEMS_DELIVERY_FAILED,
  OrderStatus.ORDER_PARTIALLY_DELIVERED_FAILED,
  OrderStatus.ORDER_DELIVERY_FAILED,
  OrderStatus.ITEMS_REJECTED,
];

const getColorCodeForStatus = ({ orderStatus }: { orderStatus: string }) => {
  switch (orderStatus) {
    case OrderStatus.PENDING_ITEMS_DELIVERY_FAILED:
    case OrderStatus.ORDER_PARTIALLY_DELIVERED_FAILED:
    case OrderStatus.ORDER_DELIVERY_FAILED:
    case OrderStatus.CLAIMED_ITEM_DELIVERY_FAILED:
    case OrderStatus.DROPOFF_FAILED:
    case OrderStatus.PAYMENT_FAILED:
    case OrderStatus.ORDER_DELIVERY_ON_HOLD:
      return FLOATER_ORDER_STATE.ERROR;

    case OrderStatus.EXTRA_ITEM_COLLECTION_FAILED:
    case OrderStatus.CLAIMED_ITEM_COLLECTION_FAILED:
    case OrderStatus.ITEMS_PENDING_APPROVAL:
      return FLOATER_ORDER_STATE.WARNING;

    default:
      return FLOATER_ORDER_STATE.NORMAL;
  }
};
const getTextForOrderStatus = (orderInfo: Order.Order, t: TFunction<'translation'>) => {
  switch (orderInfo.statusLabel) {
    case OrderStatus.ITEMS_PENDING_APPROVAL:
      return {
        title: t('pendingItems', 'Items need your approval'),
      };
    case OrderStatus.PAYMENT_FAILED:
      return {
        title: t('paymentFailed', 'You have a failed payment'),
      };

    case OrderStatus.PENDING_ITEMS_DELIVERY_FAILED:
    case OrderStatus.ORDER_PARTIALLY_DELIVERED_FAILED:
    case OrderStatus.ORDER_DELIVERY_FAILED:
    case OrderStatus.EXTRA_ITEM_COLLECTION_FAILED:
    case OrderStatus.DROPOFF_FAILED:
    case OrderStatus.ITEMS_REJECTED:
      return {
        title: orderInfo.statusText,
        subText: t(['actionIsRequiredText', 'Your action is required.']),
      };
    default:
      return {
        title: t('activeOrders', 'You have an active order'),
      };
  }
};

export function useActiveOrders() {
  const { t } = useTranslation();
  const { data, isLoading, refetch } = usePastActiveOrders();

  useEffect(() => {
    refetch();
  }, [refetch]);

  const activeOrders = useMemo(() => {
    if (!data || isLoading) {
      return [];
    }
    return data?.activeOrders;
  }, [data?.activeOrders]);

  const ordersRequireAttention = useMemo(() => activeOrders.filter((order) => statusesToFilter.includes(order.statusLabel)), [activeOrders]);
  const getOrderInfo = useCallback(
    (order: Order.Order) => {
      const { title } = getTextForOrderStatus(order, t);
      return {
        title,
        color: getColorCodeForStatus({ orderStatus: order?.statusLabel }),
        navigateTo: order,
      };
    },
    [t]
  );
  const getHighestPriorityColor = (orderList: Order.Order[]) =>
    orderList.reduce((colorCode, order) => {
      const orderColorCode = getColorCodeForStatus({ orderStatus: order?.statusLabel });
      return Math.max(colorCode, orderColorCode);
    }, FLOATER_ORDER_STATE.WARNING);

  const activeOrderInfo = useMemo(() => {
    if (isLoading || activeOrders.length === 0) {
      return {
        title: t('orderHistory', 'Order History'),
      };
    }
    const attentionCount = ordersRequireAttention.length;
    if (attentionCount === 1) {
      return getOrderInfo(ordersRequireAttention[0]);
    }

    if (attentionCount > 1) {
      return {
        title: t(['ordersAttentionRequiredText', '{{ count }} Orders require your attention'], {
          count: attentionCount,
        })!!,
        color: getHighestPriorityColor(ordersRequireAttention),
      };
    }
    const multipleOrders = activeOrders.length > 1;
    if (multipleOrders) {
      return {
        title: t(['activeOrdersCount', 'You Have {{ count }} Active Orders'], {
          count: activeOrders.length,
        })!!,
        color: FLOATER_ORDER_STATE.NORMAL,
        navigateTo: 'list',
      };
    }
    // checked previously we have only one order.
    return getOrderInfo(activeOrders[0]);
  }, [getOrderInfo, isLoading, activeOrders, ordersRequireAttention, t]);

  return {
    activeOrderInfo,
  };
}
